import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  IconButton,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import Modal from "react-modal";
import { useEffect, useState } from "react";
import { CreditCardInfo } from "types/CreditCardInfo";
import colors from "../../../../styles/colors";
import {
  brandMapping,
  detectCardBrand,
} from "../../../../utils/creditCardBrand";
import CardPreview from "./CardPreview";
import { usePagamentoConfig } from "../../../../hooks/queries/usePagamentoConfig";
import { getClientData } from "../../../../services/auth";
import {
  obterSessionIdAntifraude,
  prepararSafraPay,
  tokenizarCartao,
} from "../../../../services/safrapay";
import {
  numberFormat,
  numberRemoveFormat,
} from "../../../../utils/numberFormat";
import {
  ModalContainer,
  PreviewContainer,
  FormContainer,
  OpenModalButton,
  CardInfoTable,
  CardInfoRow,
  CardInfoLabel,
  CardInfoValue,
} from "./styles";

interface CreditCardProps {
  setCreditCardData: (data: CreditCardInfo) => void;
  totalWithTax?: string; // Total do pedido com impostos (formatado)
  disabled?: boolean; // Se true, desabilita a interação com o componente
}

/**
 * Bandeiras que a SafraPay aceita.
 *
 * Diners, Discover, JCB, UnionPay e Maestro ficam de fora: a adquirente não as suporta e o
 * backend as recusa. Filtrar ANTES evita que o comprador digite o cartão inteiro para só
 * então descobrir que não dá — e os nomes são os mesmos que `creditCardBrand` produz.
 */
const BANDEIRAS_SAFRAPAY = [
  "Visa",
  "Master",
  "American Express",
  "Elo",
  "Hipercard",
];

// Taxas do cartão por número de parcelas
const CARD_FEES: Record<string, number> = {
  "01": 3.34,
  "02": 4.98,
  "03": 5.98,
  "04": 6.98,
  "05": 7.98,
  "06": 8.98,
};

const CreditCard = ({
  setCreditCardData,
  totalWithTax = "R$ 0,00",
  disabled = false,
}: CreditCardProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [numeroDoCartao, setNumeroDoCartao] = useState("");
  const [titular, setTitular] = useState("");
  const [cvv, setCvv] = useState("");
  const [validade, setValidade] = useState("");
  const [numeroParcelas, setNumeroParcelas] = useState("01");
  const [bandeira, setBandeira] = useState("");
  const [tokenizando, setTokenizando] = useState(false);
  const [erroTokenizacao, setErroTokenizacao] = useState("");

  const { data: pagamentoConfig } = usePagamentoConfig();
  const ehSafraPay = pagamentoConfig?.adquirente === "safrapay";
  const credenciaisSafraPay = pagamentoConfig?.safrapay ?? null;

  // Baixa o SDK assim que se sabe que o cliente é SafraPay, e não no clique de confirmar:
  // o download no meio da confirmação aparece como travamento para o comprador.
  useEffect(() => {
    if (ehSafraPay && credenciaisSafraPay) {
      prepararSafraPay(credenciaisSafraPay).catch(() => {
        // Silencioso aqui de propósito: o erro que importa é o da tokenização, no
        // confirmar, onde ele tem o que dizer ao usuário. Falhar na pré-carga não impede
        // uma nova tentativa.
      });
    }
  }, [ehSafraPay, credenciaisSafraPay]);

  // Estados para salvar dados ao abrir o modal (para restaurar em caso de cancelamento)
  const [savedData, setSavedData] = useState<{
    numeroDoCartao: string;
    titular: string;
    cvv: string;
    validade: string;
    numeroParcelas: string;
    bandeira: string;
  } | null>(null);

  // Calcula o total com imposto + taxas do cartão
  const calculateTotalWithFees = (): string => {
    const totalNumber = numberRemoveFormat(totalWithTax);
    if (totalNumber === 0) {
      return "R$ 0,00";
    }

    const feePercentage = CARD_FEES[numeroParcelas] || 0;
    const feeAmount = (totalNumber * feePercentage) / 100;
    const totalWithFees = totalNumber + feeAmount;

    return numberFormat(totalWithFees);
  };

  const calculateInstallmentValue = (): string => {
    const totalNumber = numberRemoveFormat(totalWithTax);
    if (totalNumber === 0) {
      return "R$ 0,00";
    }

    const feePercentage = CARD_FEES[numeroParcelas] || 0;
    const feeAmount = (totalNumber * feePercentage) / 100;
    const totalWithFees = totalNumber + feeAmount;
    const numberOfInstallments = Number(numeroParcelas) || 1;
    const installmentValue = totalWithFees / numberOfInstallments;

    return numberFormat(installmentValue);
  };

  // Mascara o número do cartão mostrando apenas os 4 primeiros e 4 últimos dígitos
  const maskCardNumber = (cardNumber: string): string => {
    const cleanNumber = cardNumber.replace(/\D/g, "");

    if (cleanNumber.length === 0) {
      return "";
    }

    if (cleanNumber.length <= 8) {
      // Se tiver 8 ou menos dígitos, mostra todos
      return cleanNumber;
    }

    const firstFour = cleanNumber.slice(0, 4);
    const lastFour = cleanNumber.slice(-4);
    const middleLength = cleanNumber.length - 8;
    const maskedMiddle = "*".repeat(middleLength);

    return `${firstFour} ${maskedMiddle} ${lastFour}`;
  };

  // Detecta a bandeira automaticamente quando o número do cartão muda
  useEffect(() => {
    if (numeroDoCartao) {
      const brandInfo = detectCardBrand(numeroDoCartao);
      if (brandInfo) {
        setBandeira(brandInfo.name);
      } else {
        setBandeira("");
      }
    } else {
      setBandeira("");
    }
  }, [numeroDoCartao]);

  useEffect(() => {
    // Normaliza a validade para MM/YYYY
    let validadeNormalizada = validade.replace(/\D/g, "");
    let year = validadeNormalizada.slice(2);
    if (year.length === 2) {
      year = "20" + year;
    }
    validadeNormalizada = validadeNormalizada.slice(0, 2) + "/" + year;

    // O token NÃO é propagado aqui: ele é de uso único e vale para o cartão exato que o
    // gerou. Qualquer digitação invalida o token anterior, e mandá-lo junto faria o
    // backend autorizar um cartão diferente do que está na tela.
    setCreditCardData({
      numero: numeroDoCartao,
      titular: titular,
      cvv: cvv,
      validade: validadeNormalizada,
      numeroParcelas: numeroParcelas,
      bandeira: bandeira,
    });
  }, [
    numeroDoCartao,
    titular,
    cvv,
    validade,
    numeroParcelas,
    bandeira,
    setCreditCardData,
  ]);

  // Valida se os dados do cartão estão completos e válidos
  const isValidCardData = (): boolean => {
    const cleanedNumber = numeroDoCartao.replace(/\D/g, "");
    if (cleanedNumber.length === 0) return false;

    const brandInfo = detectCardBrand(numeroDoCartao);
    const isAmex = brandInfo?.name === brandMapping["amex"]?.name;
    const expectedLength = isAmex ? 15 : 16;
    const cvvLength = isAmex ? 4 : 3;

    // Valida validade (deve ter formato MM/AAAA completo)
    const validadeCleaned = validade.replace(/\D/g, "");
    const isValidExpiry =
      validadeCleaned.length === 4 || validadeCleaned.length === 6;

    // No fluxo SafraPay a bandeira precisa estar na lista que ela aceita. Sem isto o
    // comprador só descobriria a recusa depois de confirmar e ver o pedido falhar.
    const bandeiraAceita =
      !ehSafraPay || (bandeira !== "" && BANDEIRAS_SAFRAPAY.includes(bandeira));

    return (
      cleanedNumber.length === expectedLength &&
      titular.trim().length >= 3 &&
      cvv.length === cvvLength &&
      isValidExpiry &&
      bandeira !== "" &&
      bandeiraAceita
    );
  };

  const openModal = () => {
    // Salva os dados atuais antes de abrir o modal
    setSavedData({
      numeroDoCartao,
      titular,
      cvv,
      validade,
      numeroParcelas,
      bandeira,
    });
    setIsOpen(true);
  };

  const handleConfirm = async () => {
    if (!isValidCardData()) {
      return;
    }

    setErroTokenizacao("");

    // Pagar.me: comportamento inalterado — o cartão segue para o backend como sempre.
    if (!ehSafraPay || !credenciaisSafraPay) {
      setIsOpen(false);
      return;
    }

    // SafraPay: o cartão precisa virar token AQUI. Sem token não há pagamento, então o
    // modal não fecha enquanto isso não acontecer — fechar daria ao comprador a impressão
    // de que os dados foram aceitos.
    const cliente = getClientData();

    if (!cliente?.cnpj) {
      setErroTokenizacao(
        "Não foi possível identificar o seu cadastro. Recarregue a página e tente novamente."
      );
      return;
    }

    const validadeNormalizada = (() => {
      const digitos = validade.replace(/\D/g, "");
      const ano = digitos.slice(2);
      return digitos.slice(0, 2) + "/" + (ano.length === 2 ? "20" + ano : ano);
    })();

    // Uma chamada só: com duas, o TypeScript não estreita `string | undefined` para
    // `string` no spread condicional (exactOptionalPropertyTypes).
    const sessionIdAntifraude = obterSessionIdAntifraude();

    setTokenizando(true);
    try {
      const tokenizado = await tokenizarCartao(
        {
          numero: numeroDoCartao,
          titular,
          validade: validadeNormalizada,
          cvv,
          documento: cliente.cnpj,
          bandeira,
        },
        credenciaisSafraPay
      );

      setCreditCardData({
        numero: numeroDoCartao,
        titular,
        cvv,
        validade: validadeNormalizada,
        numeroParcelas,
        bandeira,
        ...tokenizado,
        ...(sessionIdAntifraude
          ? { antifraudeSessionId: sessionIdAntifraude }
          : {}),
      });

      setIsOpen(false);
    } catch (erro) {
      setErroTokenizacao(
        erro instanceof Error
          ? erro.message
          : "Não foi possível validar o cartão. Tente novamente."
      );
    } finally {
      setTokenizando(false);
    }
  };

  const handleCancel = () => {
    // Restaura os dados salvos ou limpa tudo
    if (savedData) {
      setNumeroDoCartao(savedData.numeroDoCartao);
      setTitular(savedData.titular);
      setCvv(savedData.cvv);
      setValidade(savedData.validade);
      setNumeroParcelas(savedData.numeroParcelas);
      setBandeira(savedData.bandeira);
    } else {
      // Limpa todos os dados
      setNumeroDoCartao("");
      setTitular("");
      setCvv("");
      setValidade("");
      setNumeroParcelas("01");
      setBandeira("");
    }
    setSavedData(null);
    setIsOpen(false);
  };

  // Fechar modal sem confirmação conta como cancelamento
  const closeModal = () => {
    handleCancel();
  };

  const handleNumeroDoCartaoChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const inputValue = e.target.value;

    let cleanValue = inputValue.replace(/\D/g, "");

    const brandInfo = detectCardBrand(cleanValue);
    const isAmex = brandInfo?.name === brandMapping["amex"]?.name;
    const maxLength = isAmex ? 15 : 16;

    if (cleanValue.length > maxLength) {
      cleanValue = cleanValue.slice(0, maxLength);
    }

    let formattedValue = "";

    if (isAmex) {
      // Formato Amex: 0000 000000 00000
      for (let i = 0; i < cleanValue.length; i++) {
        if (i === 4 || i === 10) {
          formattedValue += " ";
        }
        formattedValue += cleanValue[i];
      }
    } else {
      // Formato padrão: 0000 0000 0000 0000
      for (let i = 0; i < cleanValue.length; i++) {
        if (i > 0 && i % 4 === 0) {
          formattedValue += " ";
        }
        formattedValue += cleanValue[i];
      }
    }

    setNumeroDoCartao(formattedValue);
  };

  const handleCvvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, ""); // Remove tudo que não é dígito
    const brandInfo = detectCardBrand(numeroDoCartao);
    const isAmex = brandInfo?.name === brandMapping["amex"]?.name;
    const size = isAmex ? 4 : 3;
    if (value.length > size) value = value.slice(0, size);

    setCvv(value);
  };

  const handleValidadeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const previousValue = validade;

    // Máscara para a validade no formato MM/AAAA
    let value = e.target.value.replace(/\D/g, ""); // Remove tudo que não é dígito
    if (value.length > 6) value = value.slice(0, 6);

    // Validar se mês é 1 a 12
    if (parseInt(value.slice(0, 1)) > 1) {
      value = "0" + value;
    } else if (value.length >= 2 && !value.match(/^(1[0-2]|0[1-9])/)) {
      value = previousValue;
    }

    // Adicionar barra de separação entre mês e ano
    if (value.length >= 3) {
      value = value.slice(0, 2) + "/" + value.slice(2);
    }
    setValidade(value);
  };

  const handleTitularChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitular(e.target.value.toUpperCase());
  };

  return (
    <>
      <div
        style={{
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? "none" : "auto",
        }}
      >
        <OpenModalButton onClick={openModal} type="button" disabled={disabled}>
          {bandeira || numeroDoCartao || titular
            ? "Editar dados do Cartão de Crédito"
            : "Inserir dados do Cartão de Crédito"}
        </OpenModalButton>
        {(bandeira || numeroDoCartao || titular) && (
          <CardInfoTable>
            <tbody>
              <CardInfoRow>
                <CardInfoLabel>Número do cartão:</CardInfoLabel>
                <CardInfoValue>{maskCardNumber(numeroDoCartao)}</CardInfoValue>
              </CardInfoRow>
              <CardInfoRow>
                <CardInfoLabel>Titular:</CardInfoLabel>
                <CardInfoValue>{titular}</CardInfoValue>
              </CardInfoRow>
              <CardInfoRow>
                <CardInfoLabel>Validade:</CardInfoLabel>
                <CardInfoValue>{validade}</CardInfoValue>
              </CardInfoRow>
              <CardInfoRow>
                <CardInfoLabel>Número de parcelas:</CardInfoLabel>
                <CardInfoValue>
                  {numeroParcelas} ({Number(numeroParcelas)} x{" "}
                  {calculateInstallmentValue()})
                </CardInfoValue>
              </CardInfoRow>
              <CardInfoRow>
                <CardInfoLabel>Bandeira:</CardInfoLabel>
                <CardInfoValue>{bandeira}</CardInfoValue>
              </CardInfoRow>
              <CardInfoRow>
                <CardInfoLabel>Valor total com imposto:</CardInfoLabel>
                <CardInfoValue>{totalWithTax}</CardInfoValue>
              </CardInfoRow>
              <CardInfoRow>
                <CardInfoLabel>
                  Total (imposto + taxas do cartão):
                </CardInfoLabel>
                <CardInfoValue>{calculateTotalWithFees()}</CardInfoValue>
              </CardInfoRow>
            </tbody>
          </CardInfoTable>
        )}
      </div>

      {isOpen && (
        <Modal
          isOpen={isOpen}
          className="modal"
          onRequestClose={closeModal}
          contentLabel="Cartão de Crédito"
          style={{
            overlay: { backgroundColor: "rgba(0, 0, 0, 0.5)" },
            content: {
              width: "90%",
              maxWidth: "900px",
              margin: "auto",
              padding: "0",
            },
          }}
          shouldCloseOnOverlayClick={true}
          shouldCloseOnEsc={true}
        >
          <div className="head">
            <h2>Cartão de Crédito</h2>
            <IconButton onClick={handleCancel} type="button">
              <ClearIcon />
            </IconButton>
          </div>

          <ModalContainer>
            <PreviewContainer>
              <CardPreview
                cardNumber={numeroDoCartao}
                holderName={titular}
                expiry={validade}
              />
            </PreviewContainer>

            <FormContainer>
              <TextField
                value={numeroDoCartao}
                onChange={handleNumeroDoCartaoChange}
                label="Número do cartão:"
                placeholder="Digite o número do cartão"
                fullWidth
                variant="outlined"
                size="small"
                sx={{ background: "white" }}
              />
              <TextField
                value={titular}
                onChange={handleTitularChange}
                label="Titular:"
                placeholder="Nome Titular (igual no cartão)"
                variant="outlined"
                size="small"
                sx={{ background: "white" }}
              />
              <Box display="flex" flexDirection="row" gap={2}>
                <TextField
                  value={validade}
                  onChange={handleValidadeChange}
                  label="Validade:"
                  placeholder="MM/AAAA"
                  variant="outlined"
                  size="small"
                  sx={{ background: "white" }}
                />
                <TextField
                  value={cvv}
                  onChange={handleCvvChange}
                  label="CVV:"
                  placeholder="CVV"
                  variant="outlined"
                  size="small"
                  sx={{ background: "white" }}
                />
                <FormControl>
                  <InputLabel>Número de parcelas:</InputLabel>
                  <Select
                    fullWidth
                    variant="outlined"
                    value={numeroParcelas}
                    onChange={e => setNumeroParcelas(e.target.value)}
                    size="small"
                    sx={{ background: "white" }}
                  >
                    {Object.keys(CARD_FEES).map(key => (
                      <MenuItem value={key} key={key}>
                        {Number(key)}x ({CARD_FEES[key]}%)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              {erroTokenizacao && (
                <Box
                  marginTop={2}
                  padding={1.5}
                  style={{
                    background: "#FDECEA",
                    color: "#B3261E",
                    borderRadius: "8px",
                    fontSize: "14px",
                  }}
                >
                  {erroTokenizacao}
                </Box>
              )}
              {ehSafraPay &&
                bandeira !== "" &&
                !BANDEIRAS_SAFRAPAY.includes(bandeira) && (
                  <Box
                    marginTop={2}
                    padding={1.5}
                    style={{
                      background: "#FDECEA",
                      color: "#B3261E",
                      borderRadius: "8px",
                      fontSize: "14px",
                    }}
                  >
                    Esta bandeira não é aceita. Utilize Visa, Mastercard,
                    American Express, Elo ou Hipercard.
                  </Box>
                )}
              <Box
                display="flex"
                flexDirection="row"
                gap={2}
                justifyContent="flex-end"
                marginTop={2}
              >
                <button
                  type="button"
                  onClick={handleCancel}
                  style={{
                    padding: "10px 20px",
                    background: colors.background.disabled,
                    color: colors.text.primary,
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "16px",
                    fontWeight: 500,
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={!isValidCardData() || tokenizando}
                  style={{
                    padding: "10px 20px",
                    background: isValidCardData()
                      ? colors.primary
                      : colors.background.disabled,
                    color: isValidCardData()
                      ? colors.text.white
                      : colors.text.secondary,
                    border: "none",
                    borderRadius: "8px",
                    cursor: isValidCardData() ? "pointer" : "not-allowed",
                    fontSize: "16px",
                    fontWeight: 500,
                    opacity: isValidCardData() ? 1 : 0.6,
                  }}
                >
                  {tokenizando ? "Validando cartão..." : "Confirmar"}
                </button>
              </Box>
            </FormContainer>
          </ModalContainer>
        </Modal>
      )}
    </>
  );
};

export default CreditCard;
